import {
  Button,
  Dialog,
  DialogSurface,
  Input,
  makeStyles,
  MessageBar,
  MessageBarBody,
  Spinner,
  Switch,
  SwitchOnChangeData,
} from "@fluentui/react-components";
import { DocumentOnePage24Regular } from "@fluentui/react-icons";
import { CellLocation } from "@silevis/reactgrid";
import { useMsal } from "@skanska/vsa-client-auth";
import {
  ChangeEvent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { VsaTableColumnDefinition } from "../../constants/DataGridColumns";
import { useApiQuery } from "../../hooks/useApiQuery";
import { useColumnWidths } from "../../hooks/useColumnWidths";
import { useGraphClient } from "../../hooks/useGraphClient";
import { useUnsavedChanges } from "../../hooks/useUnsavedChanges";
import {
  useClearUserLibraryFilterState,
  useGetUserLibraryFilterState,
  useSaveUserLibraryFilterState,
} from "../../hooks/useUserLibraryFilterState";
import { Field, ViewDefinition } from "../../models/Field";
import { QueryFilter } from "../../models/QueryFilter";
import { LibraryFilterScope } from "../../models/UserLibraryFilterState";
import { FieldColumnSizes } from "../../services/GenericDataGridService";
import { ReportType, useGetReportFile } from "../../services/ReportService";
import { MassPublishDialog } from "../activity/MassPublishDialog";
import { EntityType } from "../../services/MassPublishService";
import { ButtonWithLoading } from "../ButtonWithLoading";
import { QueryFilterTags } from "../filters/QueryFilterTags";
import { SearchFiltersDialog } from "../filters/SearchFiltersDialog";
import { ConstrainedContent } from "../layout/ConstrainedContent";
import { Paginate } from "../Paginate";
import { DraftListSkeleton } from "../skeletons/DraftListSkeleton";
import { PublishedListSkeleton } from "../skeletons/PublishedListSkeleton";
import { VsaSearchBox } from "../VsaSearchBox";
import GenericDataGrid, { IItemMetadata } from "./GenericDataGrid";
import GenericReactGrid from "./GenericReactGrid";
import { useGetUserAccessLevel } from "../../hooks/useGetUserAccessLevel";

const useStyles = makeStyles({
  root: {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
  },
  searchInput: {
    width: "300px",
  },
  editMetadataBtn: {
    marginTop: "20px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    height: "35px",
    marginBottom: "20px",
  },
  rightActions: {
    display: "flex",
    alignItems: "center",
    gap: 0,
  },
  quickEditSwitch: {
    marginRight: "10px",
  },
  leftActions: {
    display: "flex",
    gap: "20px",
  },
  excelButton: {
    marginRight: "10px",
  },
  clearFiltersButton: {
    marginRight: "10px",
  },
  excelSpinner: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
  },
  messageBar: {
    marginBottom: "10px",
    padding: "10px",
  },
  messageBarBody: {
    fontWeight: "bold",
    marginBottom: "4px",
    textWrap: "wrap",
  },
  line: {
    textWrap: "wrap",
  },
  paginationFooter: {
    display: "flex",
    justifyContent: "start",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap",
    marginTop: "20px",
  },
  paginationControls: {
    display: "flex",
    alignItems: "flex-end",
    gap: "16px",
    flexWrap: "wrap",
  },
  pageSizeInputContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginRight: "10px",
  },
  pageSizeInput: {
    width: "90px",
  },
  paginationSummary: {
    marginBottom: "6px",
  },
  pageSizeWarningContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    width: "100%",
  },
});

const DEFAULT_PAGE_SIZE = 50;
const MAX_PAGE_SIZE = 200;

export interface PaginatedListEditingOptions<T extends IItemMetadata> {
  createFormRoute?: string;
  allowQuickEdit?: boolean;
  showFileNameColumn?: boolean;
  viewDefinition?: ViewDefinition | undefined;
  onSave?: (items: T[]) => Promise<void>; // The function that is called when save is used from Quick Edit.
  savePending?: boolean; // Status for pending Quick Edit save changes.
  usedPaths?: string[] | undefined; // Handles process tags for ReactGrid component when that tag field is used for quick edit.
  entityType?: EntityType; // Mass publishing is disabled if not set.
  toolType?: string; // Tool type (e.g., "Link", "Document") for conditional field display
  isFieldReadOnly?: (entity: T, field: Field) => boolean;
  isFieldChangeBlocked?: (entity: T, field: Field, value: unknown) => boolean;
  onFieldChangeBlocked?: (entity: T, field: Field) => void;
}
interface IPaginatedList<T extends IItemMetadata> {
  apiEndpoint: string;
  defaultSortColumnName: string;
  defaultSortAscending: boolean;
  columns: VsaTableColumnDefinition<T>[];
  searchPlaceholder: string;
  postQueryTransformation?: (items: T[]) => T[];
  cacheKey: string;
  filtersEndpoint?: string;
  reportType?: ReportType;
  editingOptions?: PaginatedListEditingOptions<T>;
  storageKey?: string;
  libraryFilterScope?: LibraryFilterScope;
  selectedActions?: (selectedVersionIds: string[]) => ReactNode;
  selectionPersistenceKey?: string;
}

const normalizeQueryFilters = (filters: QueryFilter[]) => filters;

const readPersistedSelection = (key: string): string[] => {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);

    return Array.isArray(parsed)
      ? parsed.filter((id): id is string => typeof id === "string")
      : [];
  } catch {
    return [];
  }
};

export const PaginatedList = <T extends IItemMetadata>(
  props: IPaginatedList<T>
) => {
  const classes = useStyles();
  const {
    apiEndpoint,
    defaultSortColumnName,
    defaultSortAscending,
    columns,
    searchPlaceholder,
    postQueryTransformation,
    cacheKey,
    filtersEndpoint,
    reportType,
    editingOptions,
    storageKey,
    libraryFilterScope,
    selectedActions,
    selectionPersistenceKey,
  } = props;

  const {
    data: savedLibraryFilterState,
    isLoading: loadingSavedLibraryFilterState,
  } = useGetUserLibraryFilterState(libraryFilterScope);
  const { mutateAsync: saveLibraryFilterState } =
    useSaveUserLibraryFilterState();
  const { mutateAsync: clearLibraryFilterState } =
    useClearUserLibraryFilterState(libraryFilterScope);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { graphClient } = useGraphClient();
  const pageSizeParam = searchParams.get("pageSize");
  const parsedPageSize = pageSizeParam ? parseInt(pageSizeParam, 10) : NaN;
  const pageSize =
    Number.isInteger(parsedPageSize) && parsedPageSize > 0
      ? Math.min(parsedPageSize, MAX_PAGE_SIZE)
      : DEFAULT_PAGE_SIZE;

  const {
    data: itemsResult,
    isLoading: itemsLoading,
    isRefetching: refetchingItems,
    setQuery,
    setCurrentPage,
    setSortBy,
    setSortByAscending,
    setFilters,
    filters,
    query,
    sortBy,
    sortByAscending,
    initialStateApplied,
  } = useApiQuery<T>(
    apiEndpoint,
    defaultSortColumnName,
    defaultSortAscending,
    cacheKey,
    [],
    pageSize,
    {
      initialState: savedLibraryFilterState,
      useInitialState: Boolean(libraryFilterScope),
      isInitialStateLoading: loadingSavedLibraryFilterState,
    }
  );

  const accessLevel = useGetUserAccessLevel();
  const lastPersistedLibraryFilterState = useRef<string | undefined>(undefined);
  const skippedInitialLibraryFilterPersist = useRef(false);

  const {
    columnSizingOptions: persistedColumnSizingOptions,
    handleColumnResize,
    gridKey,
  } = useColumnWidths(storageKey, FieldColumnSizes);

  const currentItems = useMemo<T[]>(() => {
    if (!itemsResult || itemsLoading || refetchingItems) {
      return [];
    }

    if (postQueryTransformation) {
      return postQueryTransformation(itemsResult.items);
    }

    return itemsResult.items;
  }, [itemsResult, itemsLoading, refetchingItems, postQueryTransformation]);

  const selectionResetKey = useMemo(() => {
    return currentItems.map((item) => item.versionId).join("|");
  }, [currentItems]);

  const resetSelectionState = () => {
    setSelectedEntities([]);
    setFilteredEntities([]);
    setShowCheckoutWarning(false);
  };

  const handleSearch = (value: string) => {
    resetSelectionState();
    setQuery(value);
    setPage(1);
  };

  const handleFiltersChange = (value: QueryFilter[]) => {
    resetSelectionState();
    setFilters(normalizeQueryFilters(value));
    setPage(1);
  };

  const handleClearFilters = () => {
    searchParams.delete("filters");
    searchParams.delete("page");

    setSearchParams(searchParams, {
      preventScrollReset: true,
    });

    handleFiltersChange([]);
  };

  const handleSortChange = (column?: string, ascending?: boolean) => {
    resetSelectionState();

    if (column) {
      setSortBy(column);
    }

    if (ascending !== undefined) {
      setSortByAscending(ascending);
    }

    setReactGridFocusLocation(undefined);
  };

  const [gridUpdatedEntities, setGridUpdatedEntities] = useState<T[]>([]);
  const [reactGridFocusLocation, setReactGridFocusLocation] = useState<
    CellLocation | undefined
  >(undefined);
  const [quickEditEnabled, setQuickEditEnabled] = useState<boolean>(false);
  const [showCheckoutWarning, setShowCheckoutWarning] =
    useState<boolean>(false);
  const [showPageSizeWarning, setShowPageSizeWarning] =
    useState<boolean>(false);

  const [selectedEntities, setSelectedEntities] = useState<string[]>(() =>
    selectionPersistenceKey
      ? readPersistedSelection(selectionPersistenceKey)
      : []
  );
  const [filteredEntities, setFilteredEntities] = useState<T[]>([]);
  const [quickEditFilterIds, setQuickEditFilterIds] = useState<string[] | null>(
    null
  );
  const [quickEditSnapshot, setQuickEditSnapshot] = useState<T[] | null>(null);
  const hasUnsavedQuickEdits =
    quickEditEnabled &&
    gridUpdatedEntities.some((updatedItem) => {
      const originalItem = quickEditSnapshot?.find(
        (item) => item.versionId === updatedItem.versionId
      );
      return JSON.stringify(updatedItem) !== JSON.stringify(originalItem);
    });
  const invalidSingleValueField = editingOptions?.viewDefinition?.fields.find(
    (field) =>
      field.type === 0 &&
      !field.multiValue &&
      gridUpdatedEntities.some(
        (item) => (item.fieldValues?.tags[field.uniqueName]?.length ?? 0) > 1
      )
  );
  useUnsavedChanges(hasUnsavedQuickEdits, t("unsavedChangesMessage"));
  const hasAppliedPersistedSelection = useRef(
    !selectionPersistenceKey || selectedEntities.length === 0
  );
  const selectedVersionIds = useMemo(() => {
    return selectedEntities;
  }, [selectedEntities]);

  const defaultSelectedItems = useMemo(
    () => new Set<string>(selectedEntities),
    [selectedEntities]
  );

  useEffect(() => {
    if (!selectionPersistenceKey) {
      return;
    }

    window.sessionStorage.setItem(
      selectionPersistenceKey,
      JSON.stringify(selectedEntities)
    );
  }, [selectionPersistenceKey, selectedEntities]);

  useEffect(() => {
    if (!selectionPersistenceKey) {
      return;
    }

    return () => {
      window.sessionStorage.removeItem(selectionPersistenceKey);
    };
  }, [selectionPersistenceKey]);

  useEffect(() => {
    if (!selectionPersistenceKey || hasAppliedPersistedSelection.current) {
      return;
    }

    if (itemsLoading || refetchingItems || !itemsResult) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      hasAppliedPersistedSelection.current = true;
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [itemsLoading, itemsResult, refetchingItems, selectionPersistenceKey]);

  const { instance } = useMsal();
  const currentUser = instance.getActiveAccount();
  const userEntraId = currentUser?.idTokenClaims?.oid;

  const isEntityEditableByCurrentUser = (entity: T) => {
    if (!userEntraId) {
      return false;
    }

    if (!entity.createdBy) {
      return true;
    }

    return userEntraId === entity.createdBy.id;
  };

  const quickEditEntities = useMemo(() => {
    if (quickEditSnapshot) {
      return quickEditSnapshot;
    }

    if (!quickEditFilterIds) {
      return currentItems;
    }

    return currentItems.filter((item) =>
      quickEditFilterIds.includes(item.versionId)
    );
  }, [currentItems, quickEditFilterIds, quickEditSnapshot]);

  const {
    refetch: downloadReport,
    isRefetching: refetchingReport,
    isLoading: loadingReport,
  } = useGetReportFile(reportType);

  const handleDownloadReport = async () => {
    if (refetchingReport || loadingReport) return;
    try {
      const { data: reportData } = await downloadReport();
      const url = URL.createObjectURL(reportData!.blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = reportData!.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const onQuickEditClick = (
    _ev: ChangeEvent<HTMLInputElement>,
    data: SwitchOnChangeData
  ) => {
    if (
      !data.checked &&
      hasUnsavedQuickEdits &&
      !window.confirm(t("quickEditDiscardChangesMessage"))
    ) {
      return;
    }

    setQuickEditEnabled(data.checked);

    if (data.checked) {
      setQuickEditSnapshot(
        selectedEntities.length > 0
          ? currentItems.filter((item) =>
              selectedEntities.includes(item.versionId)
            )
          : currentItems
      );
      setQuickEditFilterIds(
        selectedEntities.length > 0 ? selectedEntities : null
      );
    } else {
      setGridUpdatedEntities([]);
      setQuickEditFilterIds(null);
      setQuickEditSnapshot(null);
    }
  };

  const setPage = (value: number) => {
    resetSelectionState();
    setCurrentPage(value);
    setReactGridFocusLocation(undefined);
  };

  const handlePageSizeChange = (value?: string) => {
    const nextPageSize = value ? parseInt(value, 10) : NaN;

    if (!Number.isInteger(nextPageSize) || nextPageSize <= 0) {
      setShowPageSizeWarning(false);
      return false;
    }

    setShowPageSizeWarning(nextPageSize > MAX_PAGE_SIZE);

    const normalizedPageSize = Math.min(nextPageSize, MAX_PAGE_SIZE);

    if (normalizedPageSize === pageSize) {
      return true;
    }

    const nextSearchParams = new URLSearchParams(searchParams);

    if (normalizedPageSize === DEFAULT_PAGE_SIZE) {
      nextSearchParams.delete("pageSize");
    } else {
      nextSearchParams.set("pageSize", normalizedPageSize.toString());
    }

    nextSearchParams.delete("page");

    setSearchParams(nextSearchParams, {
      preventScrollReset: true,
    });

    setPage(1);

    return true;
  };

  useEffect(() => {
    setReactGridFocusLocation(undefined);
  }, [itemsResult]);

  useEffect(() => {
    if (
      !libraryFilterScope ||
      !initialStateApplied ||
      loadingSavedLibraryFilterState
    ) {
      return;
    }

    const normalizedFilters = normalizeQueryFilters(filters);
    const stateToPersist = {
      listType: libraryFilterScope.listType,
      query: query || null,
      filters: normalizedFilters,
      sortBy: sortBy || null,
      ascending: sortByAscending,
    };
    const serializedState = JSON.stringify(stateToPersist);

    if (!skippedInitialLibraryFilterPersist.current) {
      skippedInitialLibraryFilterPersist.current = true;
      lastPersistedLibraryFilterState.current = serializedState;
      return;
    }

    if (lastPersistedLibraryFilterState.current === serializedState) {
      return;
    }

    const shouldClearState =
      !query &&
      normalizedFilters.length === 0 &&
      sortBy === defaultSortColumnName &&
      sortByAscending === defaultSortAscending;

    const timeoutId = window.setTimeout(() => {
      lastPersistedLibraryFilterState.current = serializedState;

      if (shouldClearState) {
        void clearLibraryFilterState();
        return;
      }

      void saveLibraryFilterState(stateToPersist);
    }, 500);

    return () => window.clearTimeout(timeoutId);
  }, [
    clearLibraryFilterState,
    defaultSortAscending,
    defaultSortColumnName,
    filters,
    initialStateApplied,
    libraryFilterScope,
    loadingSavedLibraryFilterState,
    query,
    saveLibraryFilterState,
    sortBy,
    sortByAscending,
  ]);

  if (
    (refetchingItems && !quickEditSnapshot) ||
    (itemsLoading && !quickEditSnapshot) ||
    (!itemsResult && !quickEditSnapshot)
  ) {
    return editingOptions ? <DraftListSkeleton /> : <PublishedListSkeleton />;
  }

  const onClickSave = async () => {
    if (editingOptions?.onSave) {
      await editingOptions.onSave(gridUpdatedEntities);
      setQuickEditSnapshot(
        (snapshot) =>
          snapshot?.map(
            (item) =>
              gridUpdatedEntities.find(
                (updatedItem) => updatedItem.versionId === item.versionId
              ) ?? item
          ) ?? null
      );
      setGridUpdatedEntities([]);
    }
  };

  const handleMassPublishSuccess = () => {
    setSelectedEntities([]);
    setGridUpdatedEntities([]);
    setQuickEditSnapshot(null);
  };

  return (
    <div className={classes.root}>
      <ConstrainedContent>
        <div className={classes.header}>
          <div className={classes.leftActions}>
            {editingOptions?.createFormRoute && accessLevel > 2 && (
              <Button
                appearance="primary"
                onClick={() =>
                  navigate(editingOptions.createFormRoute as string)
                }
              >
                {t("createNew")}
              </Button>
            )}
            {quickEditEnabled && editingOptions?.onSave && accessLevel > 2 ? (
              <ButtonWithLoading
                disabled={
                  gridUpdatedEntities.length === 0 ||
                  Boolean(invalidSingleValueField)
                }
                onClick={onClickSave}
                isLoading={editingOptions.savePending ?? false}
                text={t("saveUpdate")}
              />
            ) : (
              <></>
            )}
            {quickEditEnabled &&
              editingOptions?.entityType &&
              accessLevel > 2 && (
                <MassPublishDialog
                  versionIds={selectedVersionIds}
                  disabled={selectedVersionIds.length === 0}
                  onSuccess={handleMassPublishSuccess}
                  isQuickEditMode={true}
                  entityType={editingOptions.entityType}
                />
              )}
            {editingOptions &&
              !quickEditEnabled &&
              editingOptions?.entityType &&
              accessLevel > 2 && (
                <MassPublishDialog
                  versionIds={selectedVersionIds}
                  disabled={selectedEntities.length === 0}
                  onSuccess={handleMassPublishSuccess}
                  isQuickEditMode={false}
                  entityType={editingOptions.entityType}
                />
              )}
            {selectedActions?.(selectedVersionIds)}
          </div>
          <div className={classes.rightActions}>
            {editingOptions?.allowQuickEdit && accessLevel > 2 && (
              <Switch
                className={classes.quickEditSwitch}
                checked={quickEditEnabled}
                onChange={onQuickEditClick}
                label={
                  selectedEntities.length > 0
                    ? t("quickEditSelected")
                    : t("quickEdit")
                }
              />
            )}
            {reportType !== undefined && (
              <>
                <Button
                  className={classes.excelButton}
                  icon={<DocumentOnePage24Regular />}
                  onClick={handleDownloadReport}
                  title={t("excelReport.download")}
                >
                  {t("excelReport.download")}
                </Button>
                <Dialog open={refetchingReport || loadingReport}>
                  <DialogSurface className={classes.excelSpinner}>
                    <Spinner /> {t("excelReport.loading")}
                  </DialogSurface>
                </Dialog>
              </>
            )}

            {filtersEndpoint && filters.length > 0 && (
              <Button
                className={classes.clearFiltersButton}
                onClick={handleClearFilters}
              >
                {t("clearAllFilters")}
              </Button>
            )}
            <div className={classes.pageSizeInputContainer}>
              <span>{t("paginatePageSize")}</span>
              <Input
                key={`page-size-${pageSize}`}
                className={classes.pageSizeInput}
                defaultValue={pageSize.toString()}
                onBlur={(event) => {
                  const wasApplied = handlePageSizeChange(event.target.value);

                  if (!wasApplied) {
                    event.target.value = pageSize.toString();
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    const wasApplied = handlePageSizeChange(
                      event.currentTarget.value
                    );

                    if (!wasApplied) {
                      event.currentTarget.value = pageSize.toString();
                    }
                  }
                }}
                inputMode="numeric"
              />
            </div>
            {filtersEndpoint && (
              <SearchFiltersDialog
                key={filtersEndpoint}
                endpoint={filtersEndpoint}
                defaultSelectedFilterValues={filters}
                onFilter={handleFiltersChange}
              />
            )}
            <VsaSearchBox
              key={`search-${query}`}
              placeholder={searchPlaceholder}
              onSearch={handleSearch}
              defaultValue={query}
            />
          </div>
        </div>
      </ConstrainedContent>
      {invalidSingleValueField && (
        <ConstrainedContent>
          <MessageBar intent="warning">
            <MessageBarBody>
              {t("quickEditSingleValueRequired", {
                field: invalidSingleValueField.displayName,
              })}
            </MessageBarBody>
          </MessageBar>
        </ConstrainedContent>
      )}
      {showPageSizeWarning && (
        <ConstrainedContent>
          <MessageBar className={classes.messageBar} intent="warning">
            <div className={classes.pageSizeWarningContent}>
              <MessageBarBody>
                {t("paginateMaxPageSizeWarning", { max: MAX_PAGE_SIZE })}
              </MessageBarBody>
              <Button
                appearance="secondary"
                onClick={() => setShowPageSizeWarning(false)}
              >
                {t("paginateMaxPageSizeWarningClose")}
              </Button>
            </div>
          </MessageBar>
        </ConstrainedContent>
      )}
      {filtersEndpoint && filters && (
        <ConstrainedContent>
          <QueryFilterTags
            key={filtersEndpoint}
            filtersEndpoint={filtersEndpoint}
            selectedFilters={filters}
            onFiltersChange={handleFiltersChange}
            displayMode="row"
          />
        </ConstrainedContent>
      )}
      {showCheckoutWarning && filteredEntities.length > 0 && (
        <ConstrainedContent>
          <MessageBar className={classes.messageBar} intent="warning">
            <MessageBarBody>
              <div className={classes.messageBarBody}>
                {t("checkedOutToMassPublishBarTitle")}
              </div>
              {filteredEntities.map((entity: T, index: number) => (
                <div key={entity.versionId} className={classes.line}>
                  {index + 1}. "{entity.title}" -{" "}
                  {t("checkedOutToMessageBarText")}{" "}
                  {entity.createdBy?.displayName ||
                    entity.createdBy?.email ||
                    t("unknownUser")}
                </div>
              ))}
            </MessageBarBody>
          </MessageBar>
        </ConstrainedContent>
      )}

      {editingOptions ? (
        quickEditEnabled && editingOptions.viewDefinition ? (
          <GenericReactGrid<T>
            defaultFocusLocation={reactGridFocusLocation}
            onFocusLocationChange={setReactGridFocusLocation}
            entities={quickEditEntities}
            selectedEntityIds={selectedEntities}
            entityFields={editingOptions.viewDefinition.fields}
            onGridChanges={setGridUpdatedEntities}
            usedTagsId={editingOptions.usedPaths}
            toolType={editingOptions.toolType}
            showFileNameColumn={editingOptions.showFileNameColumn}
            entityType={editingOptions.entityType}
            graphClient={graphClient}
            isFieldReadOnly={editingOptions.isFieldReadOnly}
            isFieldChangeBlocked={editingOptions.isFieldChangeBlocked}
            onFieldChangeBlocked={editingOptions.onFieldChangeBlocked}
            onSelectionChange={(ids) => {
              const editableIds: string[] = [];
              const nonEditableEntities: T[] = [];

              ids.forEach((id) => {
                const activity = currentItems.find((a) => a.versionId === id);
                if (activity) {
                  if (isEntityEditableByCurrentUser(activity)) {
                    editableIds.push(id);
                  } else {
                    nonEditableEntities.push(activity);
                  }
                }
              });
              if (nonEditableEntities.length > 0) {
                setFilteredEntities(nonEditableEntities);
                setShowCheckoutWarning(true);
              } else {
                setFilteredEntities([]);
                setShowCheckoutWarning(false);
              }

              setSelectedEntities(ids);
            }}
          />
        ) : (
          <GenericDataGrid<T>
            key={`${gridKey}-${selectionResetKey}`}
            columnSizingOptions={persistedColumnSizingOptions}
            onColumnResize={handleColumnResize}
            items={currentItems}
            getRowId={(entity) => entity.versionId}
            columns={columns}
            onSortChange={handleSortChange}
            defaultSelectedItems={defaultSelectedItems}
            onSelectionChange={(selectedIds) => {
              if (
                !hasAppliedPersistedSelection.current &&
                selectedEntities.length > 0
              ) {
                return;
              }
              const editableIds: string[] = [];
              const nonEditableEntities: T[] = [];

              selectedIds.forEach((id) => {
                const activity = currentItems.find(
                  (e) => e.versionId === String(id)
                );
                if (activity) {
                  if (isEntityEditableByCurrentUser(activity)) {
                    editableIds.push(String(id));
                  } else {
                    nonEditableEntities.push(activity);
                  }
                }
              });

              if (nonEditableEntities.length > 0) {
                setFilteredEntities(nonEditableEntities);
                setShowCheckoutWarning(true);
              } else {
                setFilteredEntities([]);
                setShowCheckoutWarning(false);
              }

              setSelectedEntities(selectedIds.map((id) => String(id)));
            }}
          />
        )
      ) : (
        <GenericDataGrid<T>
          key={`${gridKey}-${selectionResetKey}`}
          columnSizingOptions={persistedColumnSizingOptions}
          onColumnResize={handleColumnResize}
          items={currentItems}
          getRowId={(item) => item.versionId}
          columns={columns}
          onSortChange={handleSortChange}
          enableSelection
          defaultSelectedItems={defaultSelectedItems}
          onSelectionChange={(selectedIds) => {
            if (
              !hasAppliedPersistedSelection.current &&
              selectedEntities.length > 0
            ) {
              return;
            }

            setSelectedEntities(selectedIds.map((id) => String(id)));
          }}
        />
      )}

      <ConstrainedContent>
        <div className={classes.paginationFooter}>
          <div className={classes.paginationControls}>
            <div className={classes.paginationSummary}>
              {t("paginateShowingItems", {
                displayed:
                  refetchingItems && quickEditSnapshot
                    ? quickEditSnapshot.length
                    : currentItems.length,
                total: itemsResult?.totalCount ?? quickEditEntities.length,
              })}
            </div>
          </div>
          {itemsResult && itemsResult.numberOfPages > 1 && (
            <Paginate
              pageCount={itemsResult.numberOfPages}
              onPageClick={setPage}
            />
          )}
        </div>
      </ConstrainedContent>
    </div>
  );
};
